import * as React from "react";

import { Box, type BoxProps } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Icon, type IconName } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { cva, type VariantProps } from "../../lib/cva";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

/**
 * The inline-edit ROW family: a hover-washed row whose value cell flips between
 * a read trigger and an editing cell. Its one consumer is the issue view's
 * custom-field table.
 *
 * `InlineRow` and `InlineValueCell` each host an absolutely-positioned child,
 * so both declare `relative` explicitly — Box is `position: static`.
 */

// No fill: the row is a region of whatever it sits on, and the wash below
// is what makes it read as a row.
//
// The row's current wash is exported as `--row-wash` — the same variable a
// DataTable row maintains — so a descendant that must paint an opaque copy
// of the row's composited color (the value-actions pill) can rebuild it
// without knowing which host it is in. The surface layer below paints FROM
// the variable, so the two can never disagree — an opacity toggle on the
// layer itself would strand the pill with a variable no state maintains.
const INLINE_ROW_CLASS =
  "group/inline-row relative overflow-visible [--row-wash:transparent]";

const INLINE_ROW_WASH_HOVER_CLASS = "hover:[--row-wash:var(--wash-selected)]";

const INLINE_ROW_WASH_ON_CLASS = "[--row-wash:var(--wash-selected)]";

// The wash bleeds 12px past the row's content edge on both sides — the same
// `base` bleed a collapsible stack-card header capsule borrows from the page
// gutter, so a hovered row and the hovered section header above it share one
// left edge. The 1px vertical bleed covers the neighbouring dividers.
const INLINE_ROW_SURFACE_CLASS =
  "pointer-events-none absolute -inset-x-3 -inset-y-px rounded-md bg-(--row-wash)";

// The body holds the row's content AND its dividers. A divider is chrome
// BETWEEN rows, not a row's content: it paints as a 1px pseudo on the body's
// last pixel row (`after:`), never as a border that takes a pixel out of the
// row's box — so a value cell inset 2px from a 36px pitch centres exactly,
// and the hairline lands in the middle of the 4px gap between two rows'
// rings. The body is positioned for the pseudo's sake; the wash (the row's
// absolutely positioned first child) paints under it, and the positioned
// content (`InlineRowContent`, `InlineValueCell`) stays above the wash.
//
// The dividers around a lit row go: a highlighted row drops its own and the
// row before it drops its bottom one (`beforeHighlighted`), and the pointer
// does the same through CSS — the row's own divider under `group-hover`, the
// previous row's through `:has(+ :hover)` on the sibling — so the wash is a
// clean capsule with no hairline running through its translucent fill.
const INLINE_ROW_BODY_CLASS =
  "relative self-stretch " +
  "after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-px " +
  "group-hover/inline-row:after:bg-transparent " +
  "in-[[data-inline-row]:has(+[data-inline-row]:hover)]:after:bg-transparent";

const INLINE_ROW_DIVIDER_CLASS = "after:bg-border";
const INLINE_ROW_DIVIDER_OFF_CLASS = "after:bg-transparent";

// A list's first row closes its top edge with the same hairline the rows
// below draw at their bottoms (`before:`), inset by the same gutter, and
// yields to the same wash — a hovered first row is a capsule like any other.
const INLINE_ROW_TOP_DIVIDER_CLASS =
  "before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-border " +
  "group-hover/inline-row:before:bg-transparent";
const INLINE_ROW_TOP_DIVIDER_OFF_CLASS = "before:bg-transparent";

const INLINE_ROW_CONTENT_CLASS = "relative z-10";

export const inlineRowActionsVariants = cva("", {
  variants: {
    visibility: {
      hoverFocus:
        "opacity-0 pointer-events-none group-hover/inline-row:opacity-100 group-hover/inline-row:pointer-events-auto group-has-focus-visible/inline-row:opacity-100 group-has-focus-visible/inline-row:pointer-events-auto",
      hover:
        "opacity-0 pointer-events-none group-hover/inline-row:opacity-100 group-hover/inline-row:pointer-events-auto",
      valueHoverFocus:
        "opacity-0 pointer-events-none group-hover/inline-value-cell:opacity-100 group-hover/inline-value-cell:pointer-events-auto group-has-focus-visible/inline-value-cell:opacity-100 group-has-focus-visible/inline-value-cell:pointer-events-auto",
      always: "opacity-100 pointer-events-auto",
      hidden: "opacity-0 pointer-events-none",
    },
    // Hidden actions take no space instead of holding their slots — for a
    // value that must not truncate around invisible buttons.
    collapse: { true: "", false: "" },
  },
  compoundVariants: [
    { collapse: true, visibility: "hidden", className: "hidden" },
    {
      collapse: true,
      visibility: "valueHoverFocus",
      className:
        "hidden group-hover/inline-value-cell:flex group-has-focus-visible/inline-value-cell:flex",
    },
    {
      collapse: true,
      visibility: "hoverFocus",
      className:
        "hidden group-hover/inline-row:flex group-has-focus-visible/inline-row:flex",
    },
    { collapse: true, visibility: "hover", className: "hidden group-hover/inline-row:flex" },
  ],
  defaultVariants: {
    visibility: "hoverFocus",
    collapse: false,
  },
});

export type InlineRowActionsVisibility = NonNullable<
  VariantProps<typeof inlineRowActionsVariants>["visibility"]
>;

// The focus treatment is `has-focus-visible`, not `focus-within`. A read-state
// cell whose trigger opens an overlay (a select, a date picker, a member picker)
// keeps that trigger mounted, and closing the overlay hands focus back to it —
// under `focus-within` the cell then stays lit and its actions stay revealed
// with the pointer nowhere near it. A text cell never showed this because
// committing unmounts the input. `:focus-visible` is exactly the distinction:
// a mouse-clicked button does not match it, a focused text input and a
// keyboard-reached trigger both do.
//
// Hover is an OUTLINE, not a wash — the ROW washes on hover (the layer above),
// and the cell's outline over that wash is what reads as "this value is its own
// press target". Same grammar as the data-table's cell trigger. The ring is
// `--border-strong`, not `--border`: it always draws ON a washed row, and in
// dark mode the wash lifts the row to nearly the base border's own lightness.
const INLINE_VALUE_CELL_CLASS =
  "group/inline-value-cell flex-1 min-w-0 w-full h-full self-stretch flex-row items-center overflow-hidden border border-transparent transition-[border-color] hover:border-(--border-strong) has-focus-visible:border-(--border-strong)";

const INLINE_VALUE_CELL_BLEED_END_CLASS = "min-w-[calc(100%+0.5rem)] pr-2";

// The fill treatment: no outline promise — the value sits in a row of ghost
// selector triggers and must hover like them, so an `InlineValueFillGroup`
// spanning the row paints the ghost fill across the value and its actions.
// The group class stays: the hover-revealed value actions key off it. No
// border, reserved or otherwise: the rest inset compensates for the editing
// ring's pixel instead (the value cells pad the display 1px wider than the
// editor), so the fill spans the full cell height.
const INLINE_VALUE_CELL_FILL_CLASS =
  "group/inline-value-cell flex-1 min-w-0 w-full h-full self-stretch flex-row items-center overflow-hidden";

const INLINE_VALUE_TRIGGER_CLASS =
  "flex-1 min-w-0 h-full self-stretch flex-row items-center overflow-hidden rounded-none border-0 bg-transparent focus-visible:outline-none";

// The fill treatment's value + actions capsule, spanning the row: the
// hover-revealed value actions sit ON the filled pill at its end. The
// trailing 4px inset seats the last action off the pill's corner, the same
// 4px the outline grammar's absolute actions keep from the cell edge. Hidden
// actions collapse (`InlineRowActions collapse`), so an invisible button never
// truncates the value.
const INLINE_VALUE_FILL_GROUP_CLASS =
  "flex-1 min-w-0 w-full h-full self-stretch flex-row items-center rounded-md pr-1";

// Hover anywhere on the capsule — the value or its actions — paints it; a
// focus-visible trigger or action inside holds the fill for keyboard use.
const INLINE_VALUE_FILL_GROUP_PAINT_CLASS =
  "transition-colors hover:bg-wash-hover has-focus-visible:bg-wash-hover";

// An actively-edited cell wears the THEMED ring (`--brand`, the workspace
// accent — brand, not primary: primary ships neutral in this system), the
// same open-state grammar as the data-table's popover cell and a focused
// form input. Hover stays the neutral outline; the themed ring is what marks
// "this input is live". Inside a data table it also paints the table's own
// fill (`--table-fill`, the surface as a value) so the active input reads
// opaque over the washed row — the same wash-cancel the open popover cell
// does. Standalone (the drawer's inline rows) the variable is undeclared and
// the fill stays transparent: there the row's own selected wash is the only
// background the active cell carries.
const INLINE_EDITING_CELL_CLASS =
  "flex-1 min-w-0 w-full h-full self-stretch overflow-hidden";
const INLINE_EDITING_CELL_OUTLINE_CLASS =
  "border border-brand bg-[var(--table-fill,transparent)]";

const INLINE_EDITING_CELL_BLEED_END_CLASS = "min-w-[calc(100%+0.5rem)] pr-2";

export type InlineRowProps = Omit<
  BoxProps<"div">,
  "border" | "borderColor" | "className"
> & Omittable<{
  highlighted?: boolean;
  beforeHighlighted?: boolean;
  /**
   * A gutter column seated at the row's start, OUTSIDE the divider: the
   * divider (and the content) begin after it, the wash still covers it. For
   * a hover-revealed row action (a pin, a hide) that must line up with a
   * glyph column the list's neighbours draw at the row's outer edge.
   */
  leading?: React.ReactNode;
  /** Gutter width in px (default 16). */
  leadingWidth?: number;
  /**
   * Close the row's top edge with a hairline too — for a list's FIRST row,
   * whose top has no row above it to draw one. Same inset as the bottom
   * divider (after the gutter), same yield to the row's wash.
   */
  topDivider?: boolean;
}>;

export function InlineRow({
  highlighted = false,
  beforeHighlighted = false,
  leading,
  leadingWidth = 16,
  topDivider = false,
  children,
  ...props
}: InlineRowProps): React.ReactElement {
  const suppressDivider = highlighted || beforeHighlighted;

  return (
    <Box
      direction="row"
      className={cn(
        INLINE_ROW_CLASS,
        highlighted ? INLINE_ROW_WASH_ON_CLASS : INLINE_ROW_WASH_HOVER_CLASS,
      )}
      data-inline-row=""
      {...props}
    >
      <Box className={INLINE_ROW_SURFACE_CLASS} />
      {leading !== undefined ? (
        <Box
          direction="row"
          align="center"
          justify="center"
          width={leadingWidth}
          shrink={false}
          className={INLINE_ROW_CONTENT_CLASS}
        >
          {leading}
        </Box>
      ) : null}
      <Box
        direction="row"
        fill="flex"
        minWidth={0}
        className={cn(
          INLINE_ROW_BODY_CLASS,
          suppressDivider ? INLINE_ROW_DIVIDER_OFF_CLASS : INLINE_ROW_DIVIDER_CLASS,
          topDivider && INLINE_ROW_TOP_DIVIDER_CLASS,
          topDivider && highlighted && INLINE_ROW_TOP_DIVIDER_OFF_CLASS,
        )}
      >
        {children}
      </Box>
    </Box>
  );
}

export type InlineRowContentProps = Omit<BoxProps<"div">, "className">;

export function InlineRowContent({
  children,
  ...props
}: InlineRowContentProps): React.ReactElement {
  return (
    <Box className={INLINE_ROW_CONTENT_CLASS} {...props}>
      {children}
    </Box>
  );
}

// The chip chrome: ONE opaque capsule holding every action, instead of a
// free-standing outline chip per action. Actions that float over a value's
// tail (the table grammar) read as one piece of chrome landing on the text,
// and the capsule is narrower than the row of chips it replaces — no gap
// between actions, one hairline around all of them. The capsule takes the
// outline button's own recipe (page fill, `--input` hairline, the same
// hair-thin shadow) so it sits on a washed row exactly as one chip did; a
// 1px inset seats the 24px ghost buttons inside the hairline, so the capsule
// is 28px tall — the in-row action rung the identity cell's separate chips
// stand on.
const INLINE_ROW_ACTIONS_CHIP_CLASS =
  "rounded-md border border-input bg-background not-dark:bg-clip-padding p-px shadow-xs/5";
// The ghost capsule: the same hairline and 1px inset, no fill swap and no
// shadow, for actions that REST on a surface (a card footer) rather than
// float over text. One hairline around the cluster instead of one per action.
const INLINE_ROW_ACTIONS_CHIP_GHOST_CLASS =
  "rounded-md border border-input bg-transparent p-px";

// The button-side of the chip recipe: a ghost wash inside the capsule, on
// the capsule's inner radius (its 6px less the hairline and the 1px inset).
const INLINE_VALUE_ACTION_IN_CHIP_CLASS = "rounded-sm before:rounded-sm";

const InlineRowActionsChipContext = React.createContext(false);

export type InlineRowActionsChrome = "chip" | "chip-ghost";

export type InlineRowActionsProps = BoxProps<"div"> & Omittable<{
  visibility?: InlineRowActionsVisibility;
  /** Hidden actions take no space (display, not opacity). */
  collapse?: boolean;
  /** `chip` fuses the actions into one bordered capsule on the page fill —
   *  the outline button's recipe, for actions floating over a value's text.
   *  `chip-ghost` is the same capsule with no fill, for a cluster resting on
   *  its host surface. Inside either, a
   *  button reads its chrome through `useInlineRowActionChrome` and becomes
   *  a ghost on the capsule. Omit for the in-flow row, where each action
   *  carries its own chrome. */
  chrome?: InlineRowActionsChrome;
}>;

export function InlineRowActions({
  visibility,
  collapse = false,
  chrome,
  className,
  children,
  ...props
}: InlineRowActionsProps): React.ReactElement {
  const chip = chrome != null;
  return (
    <InlineRowActionsChipContext.Provider value={chip}>
      <Box
        direction="row"
        align="center"
        justify="center"
        gap={chip ? "none" : "xs"}
        className={cn(
          inlineRowActionsVariants({ visibility, collapse }),
          chrome === "chip" && INLINE_ROW_ACTIONS_CHIP_CLASS,
          chrome === "chip-ghost" && INLINE_ROW_ACTIONS_CHIP_GHOST_CLASS,
          className,
        )}
        {...props}
      >
        {children}
      </Box>
    </InlineRowActionsChipContext.Provider>
  );
}

export type InlineRowActionChrome = Omittable<{
  variant: "ghost" | "outline";
  className?: string;
}>;

/**
 * The chrome a button inside `InlineRowActions` wears. In flow it keeps its
 * own outline edge; inside a capsule the capsule owns the hairline, so the
 * button is a ghost on the capsule's inner radius. Any action button rendered into a capsule — the library's
 * `InlineValueAction` or a consumer's own trigger — reads this, so a cluster
 * fuses without the consumer knowing which chrome the host chose.
 */
export function useInlineRowActionChrome(): InlineRowActionChrome {
  const inChip = React.useContext(InlineRowActionsChipContext);
  return inChip
    ? { variant: "ghost", className: INLINE_VALUE_ACTION_IN_CHIP_CLASS }
    : { variant: "outline" };
}

export type InlineValueActionProps = Omittable<{
  icon: IconName;
  /** Accessible name ("Copy email", "Clear value"). */
  label: string;
  /** Hover tooltip: a bare verb ("Copy") — the host row names the property. */
  tooltip: string;
  onSelect: () => void;
}>;

/**
 * One hover-revealed value action on the 24px rung a data-table cell and an
 * inline property row both use. In flow it is a squared outline chip — its
 * own opaque fill is what reads over the value text, one layer, one radius.
 * Inside an `InlineRowActions chrome="chip"` capsule the capsule owns the
 * fill and hairline, and the action is a ghost button on it. The click never
 * reaches the value trigger underneath.
 */
export function InlineValueAction({
  icon,
  label,
  tooltip,
  onSelect,
}: InlineValueActionProps): React.ReactElement {
  const chrome = useInlineRowActionChrome();
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant={chrome.variant}
            size="icon-xs"
            shape="square"
            className={chrome.className}
            aria-label={label}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              onSelect();
            }}
          >
            <Icon name={icon} size="md" color="muted" />
          </Button>
        }
      />
      <TooltipPopup side="top">
        <Text size="xs">{tooltip}</Text>
      </TooltipPopup>
    </Tooltip>
  );
}

export type InlineValueCellProps = Omit<BoxProps<"div">, "className"> & Omittable<{
  bleedEnd?: boolean;
  /** Cell corners. Omit for the compact row treatment. */
  shape?: "square" | "pill";
  /** Rest hover treatment. `outline` (default) rings the full cell — the
   *  table grammar, where the ring is the edit promise. `fill` drops the ring
   *  and lets an `InlineValueFillGroup` paint the ghost fill across the row —
   *  the property-row grammar, matching the ghost selector triggers beside it.
   *  `none` paints nothing of its own: the HOST washes on hover (a bordered
   *  token tinting edge to edge, glyph included), the cell stays full-width
   *  and its actions pin to the end edge as in the outline grammar. */
  hover?: "outline" | "fill" | "none";
  /** Pins the hover ring on — for a cell whose picker overlay is open, where
   *  the pointer (and focus) have left for the popup but the cell is still
   *  the active field. Same neutral ring as hover, never a themed one.
   *  Outline treatment only. */
  active?: boolean;
}>;

export function InlineValueCell({
  bleedEnd = false,
  shape = "square",
  hover = "outline",
  active = false,
  children,
  ...props
}: InlineValueCellProps): React.ReactElement {
  return (
    <Box
      direction="row"
      align="center"
      position="relative"
      className={cn(
        hover === "outline" ? INLINE_VALUE_CELL_CLASS : INLINE_VALUE_CELL_FILL_CLASS,
        shape === "pill" ? "rounded-full" : "rounded-sm",
        hover === "outline" && active && "border-(--border-strong)",
        bleedEnd && INLINE_VALUE_CELL_BLEED_END_CLASS,
      )}
      {...props}
    >
      {children}
    </Box>
  );
}

export type InlineValueFillGroupProps = Omit<BoxProps<"div">, "className"> &
  Omittable<{
    /** Paint the ghost fill on hover/focus. Off for a read-only value, which
     *  still holds its actions but promises no edit. */
    paint?: boolean;
  }>;

/**
 * The fill treatment's value capsule: a row spanning the cell that holds the
 * value (trigger or display) and its hover-revealed actions in flow, so the
 * actions reveal INSIDE the filled area. The group owns the fill.
 */
export function InlineValueFillGroup({
  paint = true,
  children,
  ...props
}: InlineValueFillGroupProps): React.ReactElement {
  return (
    <Box
      direction="row"
      align="center"
      className={cn(
        INLINE_VALUE_FILL_GROUP_CLASS,
        paint && INLINE_VALUE_FILL_GROUP_PAINT_CLASS,
      )}
      {...props}
    >
      {children}
    </Box>
  );
}

export type InlineValueTriggerProps = Omit<BoxProps<"button">, "as">;

/** The read-state trigger inside a value cell — a real button element. */
export function InlineValueTrigger({
  children,
  disabled,
  className,
  ...props
}: InlineValueTriggerProps): React.ReactElement {
  return (
    <Box<"button">
      as="button"
      disabled={disabled}
      {...props}
      className={cn(
        INLINE_VALUE_TRIGGER_CLASS,
        className,
      )}
    >
      {children}
    </Box>
  );
}

export type InlineEditingCellProps = Omit<BoxProps<"div">, "className"> &
  Omittable<{
    bleedEnd?: boolean;
    /** Cell corners. Match the resting InlineValueCell shape. */
    shape?: "square" | "pill";
    /** The draft inside cannot be accepted as it stands: the ring turns
     *  destructive in place of the themed accent. The editor says why. */
    invalid?: boolean;
    /** The editing ring (and the table fill under it). Off when the HOST
     *  paints the editing state on its own chrome — a bordered token whose
     *  edge takes the ring's color — so the cell reserves no border and the
     *  editor sits exactly where the rest display did. `invalid` still
     *  stamps `data-invalid` for that host to read. */
    outline?: boolean;
  }>;

export function InlineEditingCell({
  bleedEnd = false,
  shape = "square",
  invalid = false,
  outline = true,
  children,
  ...props
}: InlineEditingCellProps): React.ReactElement {
  return (
    // A row that centers, like the read cell beside it. `fill="flex"` claims the
    // free space but sets no display, so without these the cell is a block and a
    // control shorter than the row height sits against its top edge.
    <Box
      direction="row"
      align="center"
      fill="flex"
      className={cn(
        INLINE_EDITING_CELL_CLASS,
        outline && INLINE_EDITING_CELL_OUTLINE_CLASS,
        shape === "pill" ? "rounded-full" : "rounded-sm",
        bleedEnd && INLINE_EDITING_CELL_BLEED_END_CLASS,
        outline && invalid && "border-destructive",
      )}
      data-invalid={invalid || undefined}
      {...props}
    >
      {children}
    </Box>
  );
}
